/**
 * Google Analytics 4 Event Tracking
 * Uses gtag.js directly for GA4
 */

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
  }
}

// GA4 Measurement ID
const GA4_MEASUREMENT_ID = 'G-224M982M93';

// Current session ID for tracking
let currentSessionId: string | null = null;

/**
 * Simple hash function for email -> user_id
 */
const hashEmail = async (email: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(email.toLowerCase().trim());
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('').substring(0, 32);
};

/**
 * Set user ID in GA (hashed email)
 */
export const setUserId = async (email: string) => {
  if (typeof window !== 'undefined' && window.gtag && email) {
    const hashedId = await hashEmail(email);
    window.gtag('config', GA4_MEASUREMENT_ID, {
      user_id: hashedId,
    });
    if (process.env.NODE_ENV === 'development') {
      console.log('[Analytics] Set user_id:', hashedId);
    }
  }
};

/**
 * Set current session ID for tracking
 */
export const setSessionId = (sessionId: string) => {
  currentSessionId = sessionId;
};

/**
 * Track event using gtag.js
 */
export const trackEvent = (
  eventName: string,
  eventParams?: Record<string, any>
) => {
  if (typeof window !== 'undefined' && window.gtag) {
    // Use assessment_session_id to avoid conflict with GA4 reserved session_id
    const params: Record<string, any> = {
      ...eventParams,
      send_to: GA4_MEASUREMENT_ID,
    };

    // Add assessment session ID if available
    if (currentSessionId) {
      params.assessment_session_id = currentSessionId;
    }

    window.gtag('event', eventName, params);

    if (process.env.NODE_ENV === 'development') {
      console.log('[Analytics]', eventName, params);
    }
  }
};

// ============================================================================
// Assessment Events
// ============================================================================

/**
 * Track when user starts the assessment (clicks start button on homepage)
 */
export const trackAssessmentStart = (assessmentType: string, language: string) => {
  trackEvent('assessment_start', {
    assessment_type: assessmentType,
    language,
  });
};

/**
 * Track when anonymous session is created
 */
export const trackSessionCreated = (sessionId: string, assessmentType: string) => {
  trackEvent('session_created', {
    assessment_type: assessmentType,
    // assessment_session_id is added automatically by trackEvent
  });
};

/**
 * Track question answered (for milestones: 25%, 50%, 75%, 100%)
 */
export const trackQuestionAnswered = (
  questionIndex: number,
  totalQuestions: number,
  assessmentType: string
) => {
  const progress = Math.round(((questionIndex + 1) / totalQuestions) * 100);

  // Track milestones: first question, 25%, 50%, 75%, last question
  const milestones = [1, 25, 50, 75, 100];
  const currentMilestone = milestones.find(m => {
    const milestoneIndex = Math.floor((m / 100) * totalQuestions);
    return questionIndex + 1 === milestoneIndex || (m === 1 && questionIndex === 0);
  });

  if (currentMilestone || questionIndex === 0 || questionIndex === totalQuestions - 1) {
    trackEvent('question_milestone', {
      question_number: questionIndex + 1,
      total_questions: totalQuestions,
      progress_percent: progress,
      milestone: currentMilestone || progress,
      assessment_type: assessmentType,
    });
  }
};

/**
 * Track when all questions are answered (before participant form)
 */
export const trackQuestionnaireComplete = (
  totalQuestions: number,
  assessmentType: string
) => {
  trackEvent('questionnaire_complete', {
    total_questions: totalQuestions,
    assessment_type: assessmentType,
  });
};

/**
 * Track when participant form is shown
 */
export const trackParticipantFormView = () => {
  trackEvent('participant_form_view');
};

/**
 * Track when participant submits their info
 */
export const trackParticipantFormSubmit = (
  hasPhone: boolean,
  hasAddress: boolean,
  position: string
) => {
  trackEvent('participant_form_submit', {
    has_phone: hasPhone,
    has_address: hasAddress,
    position,
  });
};

/**
 * Track final assessment submission
 */
export const trackAssessmentSubmit = (
  sessionId: string,
  assessmentType: string,
  totalQuestions: number
) => {
  trackEvent('assessment_submit', {
    session_id: sessionId,
    assessment_type: assessmentType,
    total_questions: totalQuestions,
  });
};

/**
 * Track assessment completion (results page view)
 */
export const trackAssessmentComplete = (
  sessionId: string,
  assessmentType: string
) => {
  trackEvent('assessment_complete', {
    session_id: sessionId,
    assessment_type: assessmentType,
  });
};

/**
 * Track homepage view
 */
export const trackHomepageView = (language: string) => {
  trackEvent('homepage_view', {
    language,
  });
};

/**
 * Track CTA button click
 */
export const trackCTAClick = (buttonLocation: string, language: string) => {
  trackEvent('cta_click', {
    button_location: buttonLocation,
    language,
  });
};

/**
 * Track campaign source from email (Brevo)
 */
export const trackCampaignSource = async (params: {
  email?: string;
  campaign?: string;
  source?: string;
  medium?: string;
}) => {
  // Set user_id if email exists
  if (params.email) {
    await setUserId(params.email);
  }

  trackEvent('campaign_visit', {
    has_email: !!params.email,
    campaign: params.campaign || 'direct',
    source: params.source || 'direct',
    medium: params.medium || 'none',
  });

  // Also set user properties for attribution
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('set', 'user_properties', {
      campaign_source: params.source || 'direct',
      campaign_name: params.campaign || 'direct',
      from_email_campaign: !!params.email,
    });
  }
};

// ============================================================================
// Lead Qualifier Quiz Events
// ============================================================================

/**
 * Track quiz landing page view
 */
export const trackQuizLandingView = (language: string) => {
  trackEvent('quiz_landing_view', {
    language,
    quiz_type: 'lead_qualifier',
  });
};

/**
 * Track quiz start (click start button)
 */
export const trackQuizStart = (language: string) => {
  trackEvent('quiz_start', {
    language,
    quiz_type: 'lead_qualifier',
  });
};

/**
 * Track quiz question view
 */
export const trackQuizQuestionView = (
  questionNumber: string,
  questionId: string,
  questionType: string,
  totalQuestions: number,
  questionIndex: number,
  language: string
) => {
  trackEvent('quiz_question_view', {
    question_number: questionNumber,
    question_id: questionId,
    question_type: questionType,
    total_questions: totalQuestions,
    progress_percent: Math.round(((questionIndex + 1) / totalQuestions) * 100),
    language,
  });
};

/**
 * Track quiz question answered
 */
export const trackQuizQuestionAnswered = (
  questionNumber: string,
  questionId: string,
  questionType: string,
  answerValue: string | string[],
  totalQuestions: number,
  questionIndex: number,
  language: string
) => {
  const answerCount = Array.isArray(answerValue) ? answerValue.length : 1;
  const hasOther = Array.isArray(answerValue)
    ? answerValue.some(v => v.startsWith('other:'))
    : answerValue.startsWith('other:');

  trackEvent('quiz_question_answered', {
    question_number: questionNumber,
    question_id: questionId,
    question_type: questionType,
    answer_count: answerCount,
    has_other: hasOther,
    progress_percent: Math.round(((questionIndex + 1) / totalQuestions) * 100),
    language,
  });
};

/**
 * Track quiz progress milestones (25%, 50%, 75%, 100%)
 */
export const trackQuizMilestone = (
  milestone: number,
  totalQuestions: number,
  language: string
) => {
  trackEvent('quiz_milestone', {
    milestone_percent: milestone,
    total_questions: totalQuestions,
    language,
  });
};

/**
 * Track quiz contact form view
 */
export const trackQuizContactFormView = (language: string) => {
  trackEvent('quiz_contact_form_view', {
    language,
    quiz_type: 'lead_qualifier',
  });
};

/**
 * Track quiz contact form field focus
 */
export const trackQuizFormFieldFocus = (fieldName: string, language: string) => {
  trackEvent('quiz_form_field_focus', {
    field_name: fieldName,
    language,
  });
};

/**
 * Track privacy policy modal opened
 */
export const trackQuizPrivacyPolicyView = (language: string) => {
  trackEvent('quiz_privacy_policy_view', {
    language,
  });
};

/**
 * Track privacy consent toggle
 */
export const trackQuizPrivacyConsent = (consented: boolean, language: string) => {
  trackEvent('quiz_privacy_consent', {
    consented,
    language,
  });
};

/**
 * Track quiz form submit attempt
 */
export const trackQuizFormSubmitAttempt = (
  hasAllFields: boolean,
  hasPrivacyConsent: boolean,
  language: string
) => {
  trackEvent('quiz_form_submit_attempt', {
    has_all_fields: hasAllFields,
    has_privacy_consent: hasPrivacyConsent,
    language,
  });
};

/**
 * Track quiz form validation error
 */
export const trackQuizFormError = (errorType: string, language: string) => {
  trackEvent('quiz_form_error', {
    error_type: errorType,
    language,
  });
};

/**
 * Track quiz completion (successful submit)
 */
export const trackQuizComplete = (
  totalQuestions: number,
  answeredQuestions: number,
  language: string
) => {
  trackEvent('quiz_complete', {
    total_questions: totalQuestions,
    answered_questions: answeredQuestions,
    completion_rate: Math.round((answeredQuestions / totalQuestions) * 100),
    language,
    quiz_type: 'lead_qualifier',
  });
};

/**
 * Track gift download click
 */
export const trackQuizGiftDownload = (language: string) => {
  trackEvent('quiz_gift_download', {
    language,
    gift_type: 'productivity_guide_pdf',
  });
};

/**
 * Track start 360 assessment from quiz
 */
export const trackQuizStart360 = (language: string, deepAnalysisAnswer: string) => {
  trackEvent('quiz_start_360', {
    language,
    deep_analysis_answer: deepAnalysisAnswer,
    source: 'quiz_completion',
  });
};

/**
 * Track quiz finish button click
 */
export const trackQuizFinish = (language: string) => {
  trackEvent('quiz_finish', {
    language,
  });
};

/**
 * Track quiz language toggle
 */
export const trackQuizLanguageToggle = (fromLanguage: string, toLanguage: string) => {
  trackEvent('quiz_language_toggle', {
    from_language: fromLanguage,
    to_language: toLanguage,
  });
};

/**
 * Track quiz back button click
 */
export const trackQuizBackButton = (
  fromStep: number | string,
  toStep: number | string,
  language: string
) => {
  trackEvent('quiz_back_button', {
    from_step: String(fromStep),
    to_step: String(toStep),
    language,
  });
};
